<template>
  <div class="geo-status" v-if="debug">
    <span>Status: {{ permissionStatus }}</span>
    <span v-if="points.length"> | Points: {{ points.length }}</span>
    <span v-if="points.length">
      | Last: {{ lastPoint.lat.toFixed(6) }},
      {{ lastPoint.lng.toFixed(6) }} ±{{ Math.round(lastPoint.acc) }}m
    </span>
    <span> | Cookie: {{ cookieName }}</span>
  </div>
</template>

<script>
export default {
  name: 'GeoWatcher',

  props: {
    cookieName: { type: String, default: 'gs' },
    intervalMs: { type: Number, default: 5000 },
    cookieDays: { type: Number, default: 10 },
    highAccuracy: { type: Boolean, default: true },
    moveThreshold: { type: Number, default: 150 }, // meters
    timeoutMs: { type: Number, default: 8000 },
    debug: { type: Boolean, default: false },
  },

  data() {
    return {
      watchId: null,
      lastUpdated: null,

      permissionObj: null, // Permissions API object
      permissionStatus: 'unknown',

      points: [], // { lat, lng, acc, ts }
    };
  },

  computed: {
    lastPoint() {
      return this.points[this.points.length - 1] || {};
    },

    cookieValue() {
      const payload = {
        status: this.permissionStatus,
        points: this.points,
      };

      return btoa(encodeURIComponent(JSON.stringify(payload)));
    },
  },

  async mounted() {
    // 1. Check global flag
    if (!window.geo_watcher_enabled) {
      if (this.debug) console.log('Geolocation disabled');
      this.permissionStatus = 'disabled';
      this.clearState();
      return;
    }

    // 2. Attempt to load cookie
    this.restoreState();

    // 3. Check geolocation support
    if (!('geolocation' in navigator)) {
      if (this.debug) console.log('Geolocation not supported');
      this.permissionStatus = 'not-supported';
      this.persistState();
      return;
    }

    // 4. Check permission API
    await this.initPermissions();

    // 5. Request permission when prompt
    if (this.permissionStatus === 'prompt') {
      setTimeout(() => {
        this.requestPermission().catch(err => {
          if (this.debug) console.log('Permission request failed', err);
        });
      }, 3000);
    }

    // 6. Start watcher when granted
    if (this.permissionStatus === 'granted') {
      this.startWatching();
    }

    // 7. Visibility
    document.addEventListener('visibilitychange', this.onVisibility);
  },

  beforeUnmount() {
    this.stopWatching();
    document.removeEventListener('visibilitychange', this.onVisibility);

    if (this.permissionObj) {
      this.permissionObj.onchange = null;
    }
  },

  methods: {
    // =======================
    // PERMISSIONS
    // =======================

    async initPermissions() {
      if (!navigator.permissions) {
        this.permissionStatus = 'unknown';
        return;
      }

      try {
        const perm = await navigator.permissions.query({ name: 'geolocation' });
        this.permissionObj = perm;

        this.setPermission(perm.state);

        perm.onchange = () => {
          this.setPermission(perm.state);

          if (perm.state === 'granted') this.startWatching();
          else this.stopWatching();
        };
      } catch {
        this.permissionStatus = 'error';
      }
    },

    setPermission(status) {
      this.permissionStatus = status;

      if (status === 'denied') {
        this.points = [];
        this.persistState();
      }
    },

    requestPermission() {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          pos => {
            if (this.debug) console.log('[requestPermission] granted');
            this.setPermission('granted');
            this.handlePosition(pos.coords);
            resolve(pos);
          },
          err => {
            if (err.code === err.PERMISSION_DENIED) {
              if (this.debug) console.log('[requestPermission] denied');
              this.setPermission('denied');
            } else {
              if (this.debug) console.log('[requestPermission] error ' + err.code);
              // No permission change here - user did not make any choice (timeout)
            }
            reject(err);
          },
          {
            enableHighAccuracy: this.highAccuracy,
            maximumAge: this.intervalMs,
            timeout: this.timeoutMs,
          }
        );
      });
    },

    // =======================
    // VISIBILITY
    // =======================

    onVisibility() {
      if (document.hidden) {
        this.stopWatching();
      } else if (this.permissionStatus === 'granted') {
        this.startWatching();
      }
    },

    // =======================
    // WATCH POSITION
    // =======================

    startWatching() {
      if (this.watchId !== null) return;

      this.takeFirstReading();

      this.watchId = navigator.geolocation.watchPosition(
        pos => {
          const now = Date.now();
          if (!this.lastUpdated || now - this.lastUpdated >= this.intervalMs) {
            this.handlePosition(pos.coords);
            this.lastUpdated = now;
          }
        },
        err => {
          if (this.debug) console.warn('watchPosition error', err);
          if (err.code === err.PERMISSION_DENIED) {
            this.setPermission('denied');
            this.stopWatching();
          }
        },
        {
          enableHighAccuracy: this.highAccuracy,
          maximumAge: this.intervalMs,
          timeout: this.timeoutMs,
        }
      );

      if (this.debug) console.log('GeoWatcher started', this.watchId);
    },

    stopWatching() {
      if (this.watchId !== null) {
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
        if (this.debug) console.log('GeoWatcher stopped');
      }
    },

    takeFirstReading() {
      navigator.geolocation.getCurrentPosition(
        pos => this.handlePosition(pos.coords),
        err => {
          if (this.debug) console.warn('Initial position failed', err);
          if (err.code === err.PERMISSION_DENIED) this.setPermission('denied');
        },
        {
          enableHighAccuracy: this.highAccuracy,
          maximumAge: this.intervalMs,
          timeout: this.timeoutMs,
        }
      );
    },

    // =======================
    // PROCESS POSITION
    // =======================

    handlePosition({ latitude, longitude, accuracy }) {
      const newPoint = {
        lat: latitude,
        lng: longitude,
        acc: accuracy,
        ts: Date.now(),
      };

      if (this.points.length === 0) {
        this.points = [newPoint];
        this.persistState();
        return;
      }

      const last = this.lastPoint;
      const dist = this.haversine(last.lat, last.lng, latitude, longitude);

      if (dist > this.moveThreshold) {
        // significant movement
        this.points.push(newPoint);
        if (this.points.length > 3) this.points.shift();

        if (this.debug) console.log(`Move ${dist.toFixed(1)}m -> new point`);
        this.persistState();
      } else if (accuracy < last.acc * 0.9) {
        // accuracy improvement
        this.points.splice(this.points.length - 1, 1, newPoint);

        if (this.debug) console.log(`Refined accuracy ${last.acc} -> ${accuracy}`);
        this.persistState();
      }
      // otherwise ignore the point
    },

    // =======================
    // UTILS
    // =======================

    haversine(lat1, lon1, lat2, lon2) {
      const R = 6371000;
      const toRad = v => (v * Math.PI) / 180;

      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);

      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) *
          Math.cos(toRad(lat2)) *
          Math.sin(dLon / 2) ** 2;

      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },

    // =======================
    // STATE PERSISTENCE (localStorage)
    // =======================
    // Geo state used to be a client-set `gs` cookie. A cookie written from JS
    // cannot carry HttpOnly, so the ASV PCI scan flagged it (#5692). It now lives
    // in localStorage (same-origin, never sent to the server), and any legacy `gs`
    // cookie is expired on load so it stops being transmitted.

    persistState() {
      try {
        localStorage.setItem(this.cookieName, this.cookieValue);
      } catch (e) {
        if (this.debug) console.warn('Geo state write failed', e);
      }
    },

    clearState() {
      try {
        localStorage.removeItem(this.cookieName);
      } catch (e) {
        if (this.debug) console.warn('Geo state clear failed', e);
      }
      this.clearLegacyCookie();
    },

    // Expire any pre-existing `gs` cookie a returning user still has from before
    // the move to localStorage, so it is no longer sent to the server (#5692).
    clearLegacyCookie() {
      try {
        document.cookie = `${this.cookieName}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      } catch (e) {
        // no-op
      }
    },

    restoreState() {
      this.clearLegacyCookie();

      let raw = null;
      try {
        raw = localStorage.getItem(this.cookieName);
      } catch (e) {
        if (this.debug) console.warn('Geo state read failed', e);
        return;
      }

      if (!raw) return;

      try {
        const decoded = JSON.parse(decodeURIComponent(atob(raw)));

        if (decoded.status) this.permissionStatus = decoded.status;
        if (Array.isArray(decoded.points)) {
          this.points = decoded.points.slice(-3);
        }
      } catch (e) {
        if (this.debug) console.warn('Geo state parse failed', e);
        this.clearState();
      }
    },
  },
};
</script>

<style scoped>
.geo-status {
  font-size: 12px;
  color: #555;
  margin-top: 0.5rem;
  font-family: monospace;
}
</style>
